import React, { useId } from 'react';
import { normalizedContains } from 'shared/utils';
import styles from './forms.module.scss';
import { ConfigProvider, Select as SelectAnt, Tag } from 'antd';
import { antdTheme as theme, colorPalette } from 'shared/constants';
import _ from 'lodash';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleX } from '@fortawesome/pro-solid-svg-icons';

/**
 * @typedef {import('antd').SelectProps
 *  & {
 *      name?: string,
 *      label?: React.ReactNode,
 *      error?: string,
 *      value?: any,
 *      onChange?: (name: string, value: any) => void | Promise<void>
 *      borderRadius?: number,
 *      noMarginBottom?: boolean,
 *      tagStyle?: React.CSSProperties,
 *      required?: boolean,
 * }} SelectProps
 */

/**
 *
 * @param {SelectProps} param0
 * @returns
 */
export function Select({
  label = null,
  value,
  onChange,
  name = '',
  placeholder = '',
  error = null,
  disabled = false,
  borderRadius = 8,
  noMarginBottom = false,
  tagStyle = {},
  variant = 'outlined',
  allowClear = true,
  id,
  required = false,
  ...props
}) {
  const generatedId = useId();
  const inputId = id || generatedId;

  const handleOnChange = value => {
    onChange?.(name, value);
  };

  const tagRender = props => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = event => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        variant="outlined"
        style={{
          margin: 3,
          whiteSpace: 'pre-wrap',
          border: `1px solid ${colorPalette.primaryMain5}`,
          ...tagStyle,
        }}>
        {label || value}
      </Tag>
    );
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Select: {
            borderRadius,
            colorBorder: theme.colorBorder,
            colorTextPlaceholder: theme.colorTextPlaceholder,
            multipleItemBorderColor: theme.colorBorder,
            multipleItemBg: colorPalette.white,
          },
          Tag: {
            defaultBg: colorPalette.primaryMain1,
            colorBorder: colorPalette.primaryMain5,
            lineHeightSM: 2,
          },
        },
      }}>
      <div className={classNames(styles.root, { [styles.noMarginBottom]: noMarginBottom })}>
        {label && <label htmlFor={inputId}>{label}</label>}
        <SelectAnt
          id={inputId}
          value={!_.isEmpty(value) ? value : undefined}
          status={error && 'error'}
          placeholder={placeholder}
          name={name}
          onChange={handleOnChange}
          filterOption={(inputValue, option) => normalizedContains(option?.label, inputValue)}
          disabled={disabled}
          showSearch
          allowClear={
            allowClear
              ? {
                  clearIcon: <FontAwesomeIcon icon={faCircleX} size="md" />,
                }
              : false
          }
          tagRender={tagRender}
          defaultValue={value}
          variant={variant}
          aria-required={required}
          {...props}
        />
        <span className={styles.error}>{error}</span>
      </div>
    </ConfigProvider>
  );
}
